/**
 * @file components/OfficeNotesPanel.js
 * @description Customer Butler - "Office notes": the staff stickies that live on
 * a customer record. "Gate code 4412." "Big dog in the yard." "Pays slow, call
 * before the truck rolls."
 *
 * These are INTERNAL. They are never rendered on a report, a share link, or a
 * client portal - the server keeps them out of every customer payload except the
 * three /office-notes routes this panel calls. The red "Internal" badge is there
 * so nobody has to guess: whatever gets typed here, a customer never sees it.
 *
 * Newest note first, each stamped with who wrote it and when (both stamped
 * server-side, so they cannot be spoofed from the browser).
 */
import React, { useCallback, useEffect, useState } from 'react';
import { apiGet, apiSend } from '../lib/api';

/**
 * @param {object} props
 * @param {string} props.customerId
 * @param {boolean} [props.mayAdd=true]     show the "Add note" box
 * @param {boolean} [props.mayTakeOff=true] show each note's "Delete" (a take-off)
 *
 * WHY (2026-10-14, James's Honest UI rule via Fable): the classic customer page
 * passes the person's Customer Butler level (HR Team page), so Look up never
 * sees an Add box and only a Manager sees Delete - the same as the server.
 */
export function OfficeNotesPanel({ customerId, mayAdd = true, mayTakeOff = true }) {
  const [notes, setNotes] = useState(null);   // null = still loading
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const load = useCallback(() => {
    apiGet(`/customer/customers/${customerId}/office-notes`)
      .then((rows) => setNotes(Array.isArray(rows) ? rows : []))
      .catch(() => setNotes([]));
  }, [customerId]);

  useEffect(() => { load(); }, [load]);

  async function add(e) {
    e.preventDefault();
    const body = text.trim();
    if (!body) return;
    setBusy(true); setError(null);
    const r = await apiSend(`/customer/customers/${customerId}/office-notes`, 'POST', { text: body });
    setBusy(false);
    if (r.ok) {
      setText('');
      setNotes((n) => [r.data, ...(n || [])]);   // newest first, no refetch needed
    } else {
      setError((r.error && r.error.message) || 'Could not save that note.');
    }
  }

  async function remove(note) {
    if (!window.confirm('Delete this office note?')) return;
    const r = await apiSend(`/customer/customers/${customerId}/office-notes/${note.id}`, 'DELETE');
    if (r.ok) setNotes((n) => (n || []).filter((x) => x.id !== note.id));
    else setError((r.error && r.error.message) || 'Could not delete that note.');
  }

  return (
    <div style={N.card}>
      <div style={N.head}>
        <div style={N.title}>Office notes</div>
        <span style={N.badge} title="These never appear on a report, a share link, or the client portal.">
          Internal - never shown to customers
        </span>
      </div>

      {mayAdd ? (
      <form onSubmit={add} style={N.form}>
        <textarea
          style={N.input}
          value={text}
          maxLength={2000}
          placeholder="Gate code, dog in the yard, pays slow, park on the road..."
          onChange={(e) => setText(e.target.value)}
          aria-label="New office note"
        />
        <button type="submit" disabled={busy || !text.trim()} style={{ ...N.btn, opacity: busy || !text.trim() ? 0.6 : 1 }}>
          {busy ? 'Saving...' : 'Add note'}
        </button>
      </form>
      ) : null}

      {error && <div style={N.error}>{error}</div>}

      {notes === null ? (
        <div style={N.muted}>Loading...</div>
      ) : notes.length === 0 ? (
        <div style={N.muted}>No office notes yet.</div>
      ) : (
        notes.map((n) => (
          <div key={n.id} style={N.note}>
            <div style={N.noteText}>{n.text}</div>
            <div style={N.noteMeta}>
              <span>{n.byName || 'Staff'} - {formatWhen(n.at)}</span>
              {mayTakeOff ? <button type="button" style={N.del} onClick={() => remove(n)}>Delete</button> : null}
            </div>
          </div>
        ))
      )}
    </div>
  );
}

/** Local date + time, falling back to the raw string if it will not parse. */
function formatWhen(at) {
  const d = new Date(at);
  return isNaN(d.getTime()) ? String(at || '') : d.toLocaleString();
}

const N = {
  card: {
    border: '1px solid var(--color-border, #E5E7EB)',
    borderLeft: '4px solid #DC2626',
    borderRadius: '10px',
    padding: '0.9rem',
    marginTop: '1.25rem',
    backgroundColor: 'var(--surface-card, #fff)',
  },
  head: { display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '0.5rem', marginBottom: '0.6rem' },
  title: { fontSize: '0.75rem', fontWeight: 700, textTransform: 'uppercase', color: 'var(--text-secondary, #6B7280)' },
  badge: {
    fontSize: '0.65rem',
    fontWeight: 700,
    textTransform: 'uppercase',
    letterSpacing: '0.03em',
    padding: '2px 8px',
    borderRadius: '9999px',
    backgroundColor: '#FEE2E2',
    color: '#991B1B',
  },
  form: { display: 'flex', gap: '0.5rem', alignItems: 'flex-start', marginBottom: '0.75rem', flexWrap: 'wrap' },
  input: {
    flex: '1 1 240px',
    minHeight: '2.6rem',
    resize: 'vertical',
    padding: '0.5rem',
    border: '1px solid var(--color-border, #E5E7EB)',
    borderRadius: '6px',
    fontSize: '0.875rem',
    fontFamily: 'inherit',
    backgroundColor: 'var(--surface-card, #fff)',
    color: 'var(--text-primary, #111)',
    boxSizing: 'border-box',
  },
  btn: {
    padding: '0.5rem 1rem',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
    fontSize: '0.85rem',
    fontWeight: 600,
    backgroundColor: 'var(--color-primary-600, #2563EB)',
    color: '#fff',
  },
  note: {
    padding: '0.55rem 0',
    borderTop: '1px solid var(--color-border, #E5E7EB)',
  },
  noteText: { fontSize: '0.875rem', whiteSpace: 'pre-wrap', color: 'var(--text-primary, #111)' },
  noteMeta: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: '0.5rem',
    fontSize: '0.72rem',
    color: 'var(--text-tertiary, #9CA3AF)',
    marginTop: '0.2rem',
  },
  del: { background: 'none', border: 'none', color: '#DC2626', cursor: 'pointer', fontSize: '0.72rem', padding: 0 },
  muted: { color: 'var(--text-secondary, #666)', fontSize: '0.85rem', padding: '0.4rem 0' },
  error: { backgroundColor: '#FEE2E2', color: '#991B1B', padding: '0.4rem 0.6rem', borderRadius: '6px', fontSize: '0.8rem', marginBottom: '0.5rem' },
};

export default OfficeNotesPanel;
